// Brief 85 §4 — QuickAddModal, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN. The statement block below is byte-identical to the body
// of `renderQuickAddModal` as it stood in App.tsx —
//   sha256 bf3c66dc44395ec6c6b518fbc79a9d4b3f2ae97e3ca79a3cfaddcf08edeac127
// — indentation included, which is why it sits at the depth it had inside
// App(). Only the imports, the Props type and the signature are new. Do not
// reformat: the indentation IS the proof.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. The dialog declares no hook (§0
// measured zero across all 21 blocks), so it moves as a component without any
// state ownership changing hands.

/* 214 — `import React`: react/react-in-jsx-scope is counted per JSX element and this is the rule's own remedy
   (189 §E's method). */
import React from "react";
import FieldSelect from "../../components/FieldSelect";
import NameAdvisor from "../../components/NameAdvisor";
import { FS } from "../../lib/tokens";
import { ModalLayout } from "../../components/SharedUI";
import { SK, pname } from "../../utils";
import { appendSavedRow } from "../../lib/savedRows";
import { findNameMatches } from "../../lib/coiMatch.mjs";
import { makeFieldRows } from "../../lib/fieldRows";
import { makeLabelTranslators } from "../../components/modalFields";
import type { TFn, TaxList } from "../../lib/taxonomyTypes";
import MenuSelect from "../MenuSelect"; // 154B §2
import OpposingCounselFields from "../modals/OpposingCounselFields"; // 214 §1 — the one form for a counsel
import { counselIdsOf, isClientCounsel, opponentIdsOfMatter, toggleId } from "../../lib/counselLinks"; // 214 §2/§4
import type { FieldSelectOption } from "../FieldSelect";

type Props = {
  _apiEnt: any;
  additionalOpponents: any[];
  arbiters: any[];
  barEntries: TaxList;
  barsList: readonly FieldSelectOption[];
  cases: any[];
  clients: any[];
  collectiveCases: any[];
  contactPersons: any[];
  contactRoleEntries: TaxList; // 214 §4 — the contact_role vocabulary
  contactRolesList: readonly FieldSelectOption[];
  countriesList: readonly FieldSelectOption[];
  countryEntries: TaxList;
  cqm: () => void;
  experts: any[];
  mediators: any[];
  opponentContacts: any[];
  opponentLawyers: any[];
  opponents: any[];
  persist: (k: string, d: unknown, fn: unknown, opts?: unknown) => Promise<unknown>;
  quickAddForm: any;
  quickAddModal: any;
  saveQuickAddParty: (sk: string, arr: unknown, fn: unknown, d: unknown, ext?: unknown, retKey?: unknown, ...rest: readonly number[][]) => Promise<unknown>;
  setAdditionalOpponents: (v: unknown) => void;
  setArbiters: (v: unknown) => void;
  setCases: (v: unknown) => void;
  setClients: (v: unknown) => void;
  setCoiPrompt: (v: unknown) => void;
  setCollectiveCases: (v: unknown) => void;
  setContactPersons: (v: unknown) => void;
  setExperts: (v: unknown) => void;
  setForm: (v: unknown) => void;
  setMediators: (v: unknown) => void;
  setOpponentContacts: (v: unknown) => void;
  setOpponentLawyers: (v: unknown) => void;
  setOpponents: (v: unknown) => void;
  t: TFn;
  titleEntries: TaxList;
  titlesList: readonly FieldSelectOption[];
  toast: (m?: string, type?: string, opts?: { action?: unknown }) => void;
  uqf: (k: string, v: unknown) => void;
};

/* 214 §2 — the matter a quick add was opened from (a case or a collective case), read without a nested ternary. */
function matterOfQuickAdd(qm: { caseId?: unknown; ccId?: unknown }, cases: readonly unknown[], ccs: readonly unknown[]): unknown {
  const hasId = (id: unknown) => (c: unknown) => !!c && typeof c === "object" && (c as { id?: unknown }).id === id;
  if (qm.caseId) return cases.find(hasId(qm.caseId)) ?? null;
  if (qm.ccId) return ccs.find(hasId(qm.ccId)) ?? null;
  return null;
}

export default function QuickAddModal({
  _apiEnt,
  additionalOpponents,
  arbiters,
  barEntries,
  barsList,
  cases,
  clients,
  collectiveCases,
  contactPersons,
  contactRoleEntries,
  contactRolesList,
  countriesList,
  countryEntries,
  cqm,
  experts,
  mediators,
  opponentContacts,
  opponentLawyers,
  opponents,
  persist,
  quickAddForm,
  quickAddModal,
  saveQuickAddParty,
  setAdditionalOpponents,
  setArbiters,
  setCases,
  setClients,
  setCoiPrompt,
  setCollectiveCases,
  setContactPersons,
  setExperts,
  setForm,
  setMediators,
  setOpponentContacts,
  setOpponentLawyers,
  setOpponents,
  t,
  titleEntries,
  titlesList,
  toast,
  uqf,
}: Props) {
    if (!quickAddModal) return null;
    // Field-label / modal-title translators — see src/components/modalFields.
    const { _fl, _mt } = makeLabelTranslators(t);
    // A3 (brief 65) — the three row builders live in src/lib/fieldRows, taken
    // under their old names so every call site below is unchanged.
    const {
      F: QF,
      S: QS,
      T: QT,
    } = makeFieldRows({ form: quickAddForm, update: uqf, fl: _fl, t });
    /* 214 — the form and the request read ONCE through typed aliases (this file's ESLint count may not rise). */
    const qa = quickAddForm as { partyIds?: unknown; role?: unknown; bar?: unknown };
    const qm = quickAddModal as { caseId?: unknown; ccId?: unknown; retKey?: unknown };
    const QTitSel = (
      <FieldSelect
        label={_fl("Title")}
        value={quickAddForm.title}
        onChange={(v) => uqf("title", v)}
        options={[["", "—"], ...(titlesList || [])]}
        entries={titleEntries}
        t={t}
      />
    );
    const QPF = (
      <>
        <div className="field-row">
          {QTitSel}
          {QF("First Name *", "firstName", "", 1)}
        </div>
        {QF("Last Name *", "lastName")}
      </>
    );
    const QCountry = (
      <FieldSelect
        label={_fl("Country")}
        value={quickAddForm.country}
        onChange={(v) => uqf("country", v)}
        options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...countriesList]}
        searchable
        entries={countryEntries}
        t={t}
      />
    );
    // 05r — `bar` is a CONVERTED column and these were free-text inputs.
    //
    // Prod's residue proves the cost: after the 2026-08-03 conversion the census
    // left two `bar` values, both `Bruxelles` — typed here, into a column whose
    // vocabulary spells it `Barreau de Bruxelles`. Nothing was wrong with the
    // conversion; a free-text writer on a converted column simply puts a label
    // straight back in, and those two rows are not re-pickable because the form
    // that made them offers no list to re-pick from.
    //
    // Two elements rather than eight call sites, per TitSel/QTitSel above: eight
    // is seven chances for one to drift back.
    //
    // NOT `searchable` — five entries, and the collaborator modal's bar picker
    // (the one that was already right) is a plain select. Matching it is the
    // point; a second look for the same kind is a second thing to get wrong.
    const QBarSel = (
      <FieldSelect
        label={_fl("Bar")}
        value={quickAddForm.bar}
        onChange={(v) => uqf("bar", v)}
        options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...barsList]}
        entries={barEntries}
        t={t}
      />
    );
    // Same kind, different column. `bar` sits on OpponentLawyer; `barAssociation`
    // on Collaborator, Mediator, Arbiter and AdditionalOpponent — and its label
    // is "Professional Registration", which is exactly why a census keyed on the
    // LABEL saw one group and not the other (server/src/lib/taxonomyColumns.js is
    // the authority on which columns hold which kind).
    const QBarAssoc = (
      <FieldSelect
        label={_fl("Professional Registration")}
        value={quickAddForm.barAssociation}
        onChange={(v) => uqf("barAssociation", v)}
        options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...barsList]}
        entries={barEntries}
        t={t}
      />
    );
    const QAddr = (
      <>
        {QF("Street", "street")}
        <div className="field-row">
          {QF("Postal Code", "postalCode")}
          {QF("City", "city")}
        </div>
        {QCountry}
      </>
    );
    // B30 (brief 84) — the birth/identity rows two quick-add variants render
    // identically (token-identical census pair); defined once beside QAddr.
    const birthFields = (
      <>
          {QAddr}
          <div className="field-row">
            <div className="field">
              <label>
                {_fl(t("cdd.field.dob", { fallback: "Date of birth" }))}
              </label>
              <input
                className="field-input"
                type="date"
                value={quickAddForm.dateOfBirth || ""}
                onChange={(e) => uqf("dateOfBirth", e.target.value)}
              />
            </div>
            {QF(
              t("cdd.field.pob", { fallback: "Place of birth" }),
              "placeOfBirth",
            )}
          </div>
      </>
    );
    // B30 second piece — the ID-document date rows, the same two branches,
    // uncovered when the census recut around birthFields.
    const idDocDateFields = (
      <>
          <div className="field-row">
            <div className="field">
              <label>
                {_fl(
                  t("cdd.field.id_doc_issued_at", { fallback: "ID issued" }),
                )}
              </label>
              <input
                className="field-input"
                type="date"
                value={quickAddForm.idDocumentIssuedAt || ""}
                onChange={(e) => uqf("idDocumentIssuedAt", e.target.value)}
              />
            </div>
            <div className="field">
              <label>
                {_fl(
                  t("cdd.field.id_doc_expires_at", { fallback: "ID expires" }),
                )}
              </label>
              <input
                className="field-input"
                type="date"
                value={quickAddForm.idDocumentExpiresAt || ""}
                onChange={(e) => uqf("idDocumentExpiresAt", e.target.value)}
              />
            </div>
          </div>
      </>
    );
    let qt = "",
      qch = null,
      qsv = () => {};
    if (quickAddModal.type === "op") {
      qt = _mt("New Opponent");
      const __isCoOp = (quickAddForm.type || "company") === "company";
      qsv = () => {
        if (__isCoOp && !quickAddForm.companyName) {
          toast(
            t("toast.company_name_required", {
              fallback: "⚠ Company name is required",
            }),
          );
          return;
        }
        if (!__isCoOp && !quickAddForm.lastName) {
          toast(
            t("toast.last_name_required", {
              fallback: "⚠ Last name is required",
            }),
          );
          return;
        }
        // 213B §16 [RULED 46] — the VAT number is free: the « BE + ten digits » refusal that stood here is gone
        // (a Luxembourg number was refused; the country was never asked). Whatever is typed is stored as typed.
        // Phase 43 CORRECTION P2 (item B / D36) — the quick-add opponent path
        // bypassed svGenGuarded, so it fired NO COI check. Wrap the whole save in
        // a deferred closure and gate it behind the SAME loose/substring COI match
        // + app-styled confirm as the rMod op path (OK proceeds, Cancel aborts).
        /* 207 §1.2 — THE LINKS ARE WRITTEN AGAINST THE SERVER'S IDS, as the
           client branch below writes them since M+3.7.2 §H. This branch used
           to mint the opponent's id and its contact's (`max + 1`), send the
           contact with `opponentId` = that guess and the case with `opIds` /
           `opRoles` / `ocIds` = those guesses, all in the same tick as the two
           creates. `POST /opponents` deletes the body id and assigns its own,
           so the case either 500'd on an opponent not inserted yet or linked
           whichever row happened to hold the guessed id. Now: the opponent's
           POST, then the contact's with the REAL opponent id, then the case
           with both real ids — the order the client branch awaits. */
        type SavedParty = { id?: number | null } & Record<string, unknown>;
        const createIn = (k: string, body: unknown) =>
          (_apiEnt as Record<string, { c: (b: unknown) => Promise<SavedParty | null> }>)[k].c(body);
        const failText = (e: unknown) =>
          "⚠ " +
          ((e instanceof Error ? e.message : "") ||
            t("toast.add_failed", { fallback: "Add failed" }));
        const doSave = async () => {
        const opPayload = {
          ...quickAddForm,
          type: __isCoOp ? "company" : "individual",
          country: quickAddForm.country || null,
        };
        delete opPayload.id;
        delete opPayload.ocTitle;
        delete opPayload.ocFirstName;
        delete opPayload.ocLastName;
        delete opPayload.ocEmail;
        delete opPayload.ocPhone;
        delete opPayload.ocFunction;
        let savedOp: SavedParty | null;
        try {
          savedOp = await createIn(SK.op, opPayload);
        } catch (e) {
          toast(failText(e));
          return;
        }
        if (savedOp?.id == null) {
          toast(failText(new Error("opponent create returned no id")));
          return;
        }
        const opId = savedOp.id;
        const op = savedOp;
        setOpponents((prev: unknown) => appendSavedRow(prev, op));
        let newOcId: number | null = null;
        if (__isCoOp && (quickAddForm.ocFirstName || quickAddForm.ocLastName)) {
          const ocPayload = {
            title: quickAddForm.ocTitle || "",
            firstName: quickAddForm.ocFirstName || "",
            lastName: quickAddForm.ocLastName || "",
            email: quickAddForm.ocEmail || "",
            phone: quickAddForm.ocPhone || "",
            function: quickAddForm.ocFunction || "",
            company: quickAddForm.companyName || "",
            opponentId: opId,
          };
          let savedOc: SavedParty | null = null;
          try {
            savedOc = await createIn(SK.oc, ocPayload);
          } catch (e) {
            toast(failText(e));
          }
          if (savedOc?.id != null) {
            newOcId = savedOc.id;
            const oc = savedOc;
            setOpponentContacts((prev: unknown) => appendSavedRow(prev, oc));
          }
        }
        if (quickAddModal.caseId) {
          persist(
            SK.ca,
            cases.map((c: any) => {
              if (c.id !== quickAddModal.caseId) return c;
              const next = {
                ...c,
                opIds: [...(c.opIds || []), opId],
                opRoles: { ...(c.opRoles || {}), [opId]: [] },
              };
              if (newOcId) next.ocIds = [...(c.ocIds || []), newOcId];
              return next;
            }),
            setCases,
          );
        } else if (quickAddModal.ccId) {
          persist(
            SK.cc,
            collectiveCases.map((c: any) => {
              if (c.id !== quickAddModal.ccId) return c;
              const next = {
                ...c,
                opIds: [...(c.opIds || []), { id: opId, roles: [] }],
              };
              if (newOcId) next.ocIds = [...(c.ocIds || []), newOcId];
              return next;
            }),
            setCollectiveCases,
          );
        } else {
          setForm((p: any) => {
            const np = { ...p, opIds: [...(p.opIds || []), opId] };
            if (newOcId) np.ocIds = [...(p.ocIds || []), newOcId];
            return np;
          });
        }
        cqm();
        toast(
          newOcId
            ? t("toast.opponent_and_contact_added", {
                fallback: "Opponent and contact added",
              })
            : t("toast.opponent_added", { fallback: "Opponent added" }),
        );
        };
        // COI gate (item B): match the new opponent's name against the
        // archived-inclusive client pool (loose substring); on a hit, defer the
        // save to the app-styled OK/Cancel confirm.
        const _party = { ...quickAddForm, type: __isCoOp ? "company" : "individual" };
        if (findNameMatches(_party, clients).length) {
          setCoiPrompt({
            msg: t("party.warn.may_be_client", {
              fallback: "That adversary may have been a client",
            }),
            onConfirm: doSave,
          });
          return;
        }
        void doSave();
      };
      qch = (
        <>
          {QS("Type", "type", [
            ["company", t("type.company", { fallback: "Company" })],
            ["individual", t("type.individual", { fallback: "Individual" })],
          ])}
          {/* Phase 43 CORRECTION P2 (item B) — live COI/duplicate advisory on the
              quick-add opponent name too (reads qf, works for both name shapes). */}
          <NameAdvisor {...({} as any)}
            form={quickAddForm}
            pool={opponents}
            coiPool={clients}
            typeLabel={t("type.opponent", { fallback: "opponent" })}
            t={t}
          />
          {__isCoOp ? (
            <>
              {QF("Company Name *", "companyName", "", 1)}
              <div className="field-row">
                {/* 213B §17 [RULED 47] — no sample value in an empty field: « BE0123456789 » read as content (and §16
                    made the number free — a Belgian shape would mislead); the enterprise number keeps a FORMAT, in
                    zeros only. */}
                {QF("VAT", "vat")}
                {QF(
                  t("cdd.field.bce", { fallback: "Enterprise number" }),
                  "bceNumber",
                  "0000.000.000",
                )}
              </div>
              <div className="field-row">
                {QF("Email", "email")}
                {QF("Phone", "phone")}
              </div>
              {QF("Contact (additional)", "contact")}
              {QAddr}
              {QT("Notes", "notes")}
              <div
                style={{
                  borderTop: "1px solid var(--br)",
                  marginTop: 8,
                  paddingTop: 8,
                }}
              >
                <div
                  style={{
                    fontSize: FS.s10,
                    fontWeight: 600,
                    color: "var(--tp)",
                    marginBottom: 6,
                  }}
                >
                  {t("modal.qa.primary_contact_person", {
                    fallback: "Primary Contact Person",
                  })}{" "}
                  <span style={{ color: "var(--tm)", fontWeight: 400 }}>
                    {t("modal.qa.optional_auto_linked", {
                      fallback: "(optional — created and linked automatically)",
                    })}
                  </span>
                </div>
                <div className="field-row">
                  {/* 05n — was a HARDCODED person_title list: six <option>s
                      whose VALUE was the English label and whose text came
                      from a t() key. Invisible to 05m-b's census, which
                      counted selects reading a taxonomy list, and this one
                      reads none. It ignored every edit a firm made in
                      Settings, and after the conversion it would have written
                      "Mr." — a string TWO entries claim — into a column
                      holding `mr`. */}
                  <FieldSelect
                    label={_fl("Title")}
                    value={quickAddForm.ocTitle}
                    onChange={(v) => uqf("ocTitle", v)}
                    options={[["", "—"], ...(titlesList || [])]}
                    entries={titleEntries}
                    t={t}
                  />
                  {QF("First Name", "ocFirstName")}
                </div>
                <div className="field-row">
                  {QF("Last Name", "ocLastName")}
                  {QF("Function / Role", "ocFunction")}
                </div>
                <div className="field-row">
                  {QF("Email", "ocEmail")}
                  {QF("Phone", "ocPhone")}
                </div>
              </div>
            </>
          ) : (
            <>
              {QPF}
              {QF("National Register ID", "nationalRegister")}
              <div className="field-row">
                {QF("Email", "email")}
                {QF("Phone", "phone")}
              </div>
              {QF("Contact (additional)", "contact")}
              {QAddr}
              {QT("Notes", "notes")}
            </>
          )}
        </>
      );
    } else if (quickAddModal.type === "ocu") {
      qt = _mt("New Opp. Counsel");
      /* 214 §1/§2 [RULED 51] — ONE FORM (OpposingCounselFields: the list's order, its name check, the Notes at the
         end) and, from a matter, the party ask: the matter's opposing parties as a checkbox list, the ticked ids in
         `quickAddForm.partyIds`, handed to `saveQuickAddParty` for the LINK (never the person's row). */
      qsv = () =>
        saveQuickAddParty(
          SK.ocu,
          opponentLawyers,
          setOpponentLawyers,
          { ...quickAddForm, partyIds: undefined },
          { country: quickAddForm.country || null },
          quickAddModal.retKey,
          counselIdsOf(qa.partyIds),
        );
      const __matter = matterOfQuickAdd(qm, cases, collectiveCases);
      const __partyOpts = opponentIdsOfMatter(__matter)
        .map((id): unknown => opponents.find((x: { id?: unknown }) => x.id === id))
        .filter((o): o is { id: number } => !!o);
      const __tickedParties = counselIdsOf(qa.partyIds);
      const qParties = __matter ? (
        <div className="field" data-counsel-parties-ask="">
          <label>{t("counsel.parties.ask", { fallback: "Represents, in this matter" })}</label>
          {__partyOpts.length === 0 ? (
            <p style={{ fontSize: FS.s9, color: "var(--tm)", marginTop: 3 }}>
              {t("counsel.parties.no_opponents", { fallback: "No opposing party in this matter yet" })}
            </p>
          ) : (
            __partyOpts.map((o) => (
              <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: FS.s11, cursor: "pointer" }}>
                <input type="checkbox" checked={__tickedParties.includes(o.id)} onChange={() => { uqf("partyIds", toggleId(qa.partyIds, o.id)); }} />
                <span>{pname(o)}</span>
              </label>
            ))
          )}
        </div>
      ) : undefined;
      qch = (
        <OpposingCounselFields
          F={QF}
          T={QT}
          TitSel={QTitSel}
          BarSel={QBarSel}
          nameAdvisor={<NameAdvisor form={quickAddForm} pool={opponentLawyers} typeLabel={t("type.counsel", { fallback: "opposing counsel" })} t={t} />}
          address={QAddr}
          parties={qParties}
        />
      );
    } else if (quickAddModal.type === "ex") {
      qt = _mt("New Expert");
      qsv = () =>
        saveQuickAddParty(
          SK.ex,
          experts,
          setExperts,
          quickAddForm,
          { country: quickAddForm.country || null },
          quickAddModal.retKey,
        );
      qch = (
        <>
          <div className="field-row">
            {QTitSel}
            {QF("First Name *", "firstName", "", 1)}
          </div>
          <div className="field-row">
            {QF("Last Name *", "lastName")}
            {QF("Specialty", "spec")}
          </div>
          {QF("Office / Practice", "office")}
          <div className="field-row">
            {QF("Email", "email")}
            {QF("Phone", "phone")}
          </div>
          {QF("Professional Registration", "professionalRegistration")}
          {QAddr}
          {QT("Notes", "notes")}
        </>
      );
    } else if (quickAddModal.type === "cp") {
      qt = _mt("New Contact Person");
      const __caseCl = quickAddModal.caseId
        ? (cases.find((c: any) => c.id === quickAddModal.caseId)?.clIds || [])
            .map((id: any) => clients.find((x: any) => x.id === id))
            .filter(Boolean)
        : clients;
      qsv = () => {
        if (!quickAddForm.clientId) {
          toast(
            t("toast.select_client", { fallback: "⚠ Please select a client" }),
          );
          return;
        }
        saveQuickAddParty(
          SK.cp,
          contactPersons,
          setContactPersons,
          quickAddForm,
          { clientId: +quickAddForm.clientId, country: quickAddForm.country || null },
          quickAddModal.retKey,
        );
      };
      qch = (
        <>
          <div className="field">
            {/* 154B §2 — MenuSelect and not FieldSelect: this `.field` carries
                the "no clients linked" warning under the control. `autoFocus`
                is the prop §2 widened the primitive for. */}
            <label>{_fl("Linked to Client *")}</label>
            <MenuSelect
              className="field-input"
              value={quickAddForm.clientId ? String(quickAddForm.clientId) : ""}
              onChange={(v) => uqf("clientId", v ? +v : "")}
              autoFocus
              options={[
                [
                  "",
                  t("modal.qa.select_client", { fallback: "— Select client —" }),
                ] as const,
                ...__caseCl.map((c: any) => [String(c.id), pname(c)] as const),
              ]}
            />
            {__caseCl.length === 0 && (
              <p style={{ fontSize: FS.s9, color: "var(--am)", marginTop: 3 }}>
                {t("quickAdd.no_clients_linked", {
                  fallback:
                    "No clients linked to this case yet. Add a client first.",
                })}
              </p>
            )}
          </div>
          <div className="field-row">
            {QTitSel}
            {QF("First Name *", "firstName")}
          </div>
          <div className="field-row">
            {QF("Last Name *", "lastName")}
            {QF("Function / Role", "function")}
          </div>
          {/* 214 §4 [RULED 53] — the role (the contact_role list; a FieldSelect, never a free-text input on a
              converted column) and, for the role Avocat, the bar; the company then reads « Cabinet ». */}
          <div className="field-row" data-contact-role-row="">
            <FieldSelect
              label={_fl("Role")}
              value={qa.role as string | null | undefined}
              onChange={(v) => { uqf("role", v || null); }}
              options={[["", "—"], ...contactRolesList]}
              entries={contactRoleEntries}
              t={t}
            />
            {isClientCounsel(qa) ? (
              <FieldSelect
                label={_fl("Bar")}
                value={qa.bar as string | null | undefined}
                onChange={(v) => { uqf("bar", v || null); }}
                options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...barsList]}
                entries={barEntries}
                t={t}
              />
            ) : null}
          </div>
          <div className="field-row">
            {QF("Email", "email")}
            {QF("Phone", "phone")}
          </div>
          {isClientCounsel(qa) ? QF("Firm", "company") : QF("Company", "company")}
          {/* 189 §E — THE COMPACT FORM. A contact met on a matter is an
              ordinary contact: identity, the client, contact details, notes.
              The identity-document block (date and place of birth,
              nationality, the four ID fields — `birthFields`,
              `idDocDateFields` and the two QF pairs that stood here) belongs
              to the FULL form, ContactPersonModal, where the CDD fields are
              needed; every one of them is still on the row and editable there. */}
          {QT("Notes", "notes")}
        </>
      );
    } else if (quickAddModal.type === "oc") {
      qt = _mt("New Opp. Contact");
      const __caseOp = quickAddModal.caseId
        ? (cases.find((c: any) => c.id === quickAddModal.caseId)?.opIds || [])
            .map((id: any) => opponents.find((x: any) => x.id === id))
            .filter(Boolean)
        : opponents;
      qsv = () => {
        if (!quickAddForm.opponentId) {
          toast(
            t("toast.select_opponent", {
              fallback: "⚠ Please select an opponent",
            }),
          );
          return;
        }
        saveQuickAddParty(
          SK.oc,
          opponentContacts,
          setOpponentContacts,
          quickAddForm,
          { opponentId: +quickAddForm.opponentId, country: quickAddForm.country || null },
          quickAddModal.retKey,
        );
      };
      qch = (
        <>
          <div className="field">
            <label>{_fl("Linked to Opponent *")}</label>
            <MenuSelect
              className="field-input"
              value={quickAddForm.opponentId ? String(quickAddForm.opponentId) : ""}
              onChange={(v) => uqf("opponentId", v ? +v : "")}
              autoFocus
              options={[
                [
                  "",
                  t("modal.qa.select_opponent", {
                    fallback: "— Select opponent —",
                  }),
                ] as const,
                ...__caseOp.map((o: any) => [String(o.id), pname(o)] as const),
              ]}
            />
            {__caseOp.length === 0 && (
              <p style={{ fontSize: FS.s9, color: "var(--am)", marginTop: 3 }}>
                {t("quickAdd.no_opponents_linked", {
                  fallback:
                    "No opponents linked to this case yet. Add an opponent first.",
                })}
              </p>
            )}
          </div>
          <div className="field-row">
            {QTitSel}
            {QF("First Name *", "firstName")}
          </div>
          <div className="field-row">
            {QF("Last Name *", "lastName")}
            {QF("Function / Role", "function")}
          </div>
          <div className="field-row">
            {QF("Email", "email")}
            {QF("Phone", "phone")}
          </div>
          {QF("Company", "company")}
          {QAddr}
          {QT("Notes", "notes")}
        </>
      );
    } else if (quickAddModal.type === "me") {
      qt = _mt("New Mediator");
      qsv = () =>
        saveQuickAddParty(
          SK.me,
          mediators,
          setMediators,
          quickAddForm,
          { country: quickAddForm.country || null },
          quickAddModal.retKey,
        );
      qch = (
        <>
          <div className="field-row">
            {QTitSel}
            {QF("First Name *", "firstName", "", 1)}
          </div>
          <div className="field-row">
            {QF("Last Name *", "lastName")}
            {QF("Specialty", "specialty")}
          </div>
          {QF("Practice", "firm")}
          <div className="field-row">
            {QF("Email", "email")}
            {QF("Phone", "phone")}
          </div>
          {QBarAssoc}
          {QAddr}
          {QT("Notes", "notes")}
        </>
      );
    } else if (quickAddModal.type === "ar") {
      qt = _mt("New Arbiter");
      qsv = () =>
        saveQuickAddParty(
          SK.ar,
          arbiters,
          setArbiters,
          quickAddForm,
          { country: quickAddForm.country || null },
          quickAddModal.retKey,
        );
      qch = (
        <>
          <div className="field-row">
            {QTitSel}
            {QF("First Name *", "firstName", "", 1)}
          </div>
          <div className="field-row">
            {QF("Last Name *", "lastName")}
            {QF("Specialty", "specialty")}
          </div>
          {QF("Chamber", "firm")}
          <div className="field-row">
            {QF("Email", "email")}
            {QF("Phone", "phone")}
          </div>
          {QBarAssoc}
          {QAddr}
          {QT("Notes", "notes")}
        </>
      );
    } else if (quickAddModal.type === "op2") {
      qt = _mt("New Other Person");
      qsv = () =>
        saveQuickAddParty(
          SK.op2,
          additionalOpponents,
          setAdditionalOpponents,
          quickAddForm,
          { country: quickAddForm.country || null },
          quickAddModal.retKey,
        );
      qch = (
        <>
          <div className="field-row">
            {QTitSel}
            {QF("First Name *", "firstName", "", 1)}
          </div>
          <div className="field-row">
            {QF("Last Name *", "lastName")}
            {QF("Specialty", "specialty")}
          </div>
          {QF("Organisation", "firm")}
          <div className="field-row">
            {QF("Email", "email")}
            {QF("Phone", "phone")}
          </div>
          {QBarAssoc}
          {QAddr}
          {QT("Notes", "notes")}
        </>
      );
    } else if (quickAddModal.type === "cl") {
      qt = _mt("New Client");
      const __isCo = (quickAddForm.type || "individual") === "company";
      // M+3.7.2 §H — await client POST, then optionally cp POST with the real
      // client id, then case PUT with the real ids. Previous fire-and-forget
      // shape raced FK constraint on caseClient + caseContactPerson createMany.
      qsv = async () => {
        if (__isCo && !quickAddForm.companyName) {
          toast(
            t("toast.company_name_required", {
              fallback: "⚠ Company name is required",
            }),
          );
          return;
        }
        if (!__isCo && !quickAddForm.lastName) {
          toast(
            t("toast.last_name_required", {
              fallback: "⚠ Last name is required",
            }),
          );
          return;
        }
        // 213B §16 [RULED 46] — the VAT number is free: the « BE + ten digits » refusal that stood here is gone
        // (a Luxembourg number was refused; the country was never asked). Whatever is typed is stored as typed.
        const clPayload = {
          ...quickAddForm,
          type: __isCo ? "company" : "individual",
          lang: quickAddForm.lang || null, // 209 §5 [RULED 18] — none when none was chosen (it was « fr »)
          country: quickAddForm.country || null,
        };
        delete clPayload.id;
        delete clPayload.cpTitle;
        delete clPayload.cpFirstName;
        delete clPayload.cpLastName;
        delete clPayload.cpEmail;
        delete clPayload.cpPhone;
        delete clPayload.cpFunction;
        let savedCl;
        try {
          savedCl = await _apiEnt[SK.cl].c(clPayload);
          if (!savedCl || savedCl.id == null)
            throw new Error("client create returned no id");
        } catch (e: any) {
          toast(
            "⚠ " +
              (e.message || t("toast.add_failed", { fallback: "Add failed" })),
          );
          return;
        }
        setClients((prev: any) => appendSavedRow(prev, savedCl));
        let savedCp: any = null;
        if (__isCo && (quickAddForm.cpFirstName || quickAddForm.cpLastName)) {
          const cpPayload = {
            title: quickAddForm.cpTitle || "",
            firstName: quickAddForm.cpFirstName || "",
            lastName: quickAddForm.cpLastName || "",
            email: quickAddForm.cpEmail || "",
            phone: quickAddForm.cpPhone || "",
            function: quickAddForm.cpFunction || "",
            company: quickAddForm.companyName || "",
            clientId: savedCl.id,
          };
          try {
            savedCp = await _apiEnt[SK.cp].c(cpPayload);
          } catch (e: any) {
            toast(
              "⚠ " +
                (e.message ||
                  t("toast.add_failed", { fallback: "Add failed" })),
            );
          }
          if (savedCp && savedCp.id != null) {
            setContactPersons((prev: any) => appendSavedRow(prev, savedCp));
          } else {
            savedCp = null;
          }
        }
        if (quickAddModal.caseId) {
          persist(
            SK.ca,
            cases.map((c: any) => {
              if (c.id !== quickAddModal.caseId) return c;
              const next = {
                ...c,
                clIds: [...(c.clIds || []), savedCl.id],
                clRoles: { ...(c.clRoles || {}), [savedCl.id]: [] },
              };
              if (savedCp) next.cpIds = [...(c.cpIds || []), savedCp.id];
              return next;
            }),
            setCases,
          );
        } else {
          setForm((p: any) => {
            const np = { ...p, clIds: [...(p.clIds || []), savedCl.id] };
            if (savedCp) np.cpIds = [...(p.cpIds || []), savedCp.id];
            return np;
          });
        }
        cqm();
        toast(
          savedCp
            ? t("toast.client_and_contact_added", {
                fallback: "Client and contact added",
              })
            : t("toast.client_added", { fallback: "Client added" }),
        );
      };
      qch = (
        <>
          {QS("Type", "type", [
            ["individual", t("type.individual", { fallback: "Individual" })],
            ["company", t("type.company", { fallback: "Company" })],
          ])}
          {__isCo ? (
            <>
              {QF("Company Name *", "companyName", "", 1)}
              <div className="field-row">
                {/* 213B §17 [RULED 47] — no sample value in an empty field: « BE0123456789 » read as content (and §16
                    made the number free — a Belgian shape would mislead); the enterprise number keeps a FORMAT, in
                    zeros only. */}
                {QF("VAT", "vat")}
                {QF(
                  t("cdd.field.bce", { fallback: "Enterprise number" }),
                  "bceNumber",
                  "0000.000.000",
                )}
              </div>
              <div className="field-row">
                {QF("Email", "email")}
                {QF("Phone", "phone")}
              </div>
              {QAddr}
              {QT("Notes", "notes")}
              <div
                style={{
                  borderTop: "1px solid var(--br)",
                  marginTop: 8,
                  paddingTop: 8,
                }}
              >
                <div
                  style={{
                    fontSize: FS.s10,
                    fontWeight: 600,
                    color: "var(--tp)",
                    marginBottom: 6,
                  }}
                >
                  {t("modal.qa.primary_contact_person", {
                    fallback: "Primary Contact Person",
                  })}{" "}
                  <span style={{ color: "var(--tm)", fontWeight: 400 }}>
                    {t("modal.qa.optional_auto_linked", {
                      fallback: "(optional — created and linked automatically)",
                    })}
                  </span>
                </div>
                <div className="field-row">
                  {/* 05n — was a HARDCODED person_title list: six <option>s
                      whose VALUE was the English label and whose text came
                      from a t() key. Invisible to 05m-b's census, which
                      counted selects reading a taxonomy list, and this one
                      reads none. It ignored every edit a firm made in
                      Settings, and after the conversion it would have written
                      "Mr." — a string TWO entries claim — into a column
                      holding `mr`. */}
                  <FieldSelect
                    label={_fl("Title")}
                    value={quickAddForm.cpTitle}
                    onChange={(v) => uqf("cpTitle", v)}
                    options={[["", "—"], ...(titlesList || [])]}
                    entries={titleEntries}
                    t={t}
                  />
                  {QF("First Name", "cpFirstName")}
                </div>
                <div className="field-row">
                  {QF("Last Name", "cpLastName")}
                  {QF("Function / Role", "cpFunction")}
                </div>
                <div className="field-row">
                  {QF("Email", "cpEmail")}
                  {QF("Phone", "cpPhone")}
                </div>
              </div>
            </>
          ) : (
            <>
              {QPF}
              {QF("National Register ID", "nationalRegisterId")}
              <div className="field-row">
                {QF("Email", "email")}
                {QF("Phone", "phone")}
              </div>
          {birthFields}
              {QF(
                t("cdd.field.nationality", { fallback: "Nationality" }),
                "nationality",
              )}
              <div className="field-row">
                {QF(
                  t("cdd.field.id_doc_type", { fallback: "ID document type" }),
                  "idDocumentType",
                  t("cdd.field.id_doc_type_ph", {
                    fallback: "passport / national_id",
                  }),
                )}
                {QF(
                  t("cdd.field.id_doc_number", {
                    fallback: "ID document number",
                  }),
                  "idDocumentNumber",
                )}
              </div>
              {idDocDateFields}
              {QT("Notes", "notes")}
            </>
          )}
        </>
      );
    }
    return <ModalLayout title={qt} onClose={cqm} onSave={qsv}>{qch}</ModalLayout>;
}
